import { useEffect } from "react"
import { useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar"
import { UnifiedSidebar } from "@/components/UnifiedSidebar"
import { ProtectedRoute } from "@/hooks/useAuth"
import { useOrganizationContext } from "@/contexts/OrganizationContext"
import { Loader2, Menu } from "lucide-react"
import { WebChatFloatingWidget } from "@/components/WebChatFloatingWidget"
import { DocAssistantWidget } from "@/components/DocAssistantWidget"
import { ProfileCompletionPopup } from "@/components/ProfileCompletionPopup"
import { FLUITY_LAST_PATH_KEY, isAppPath } from "@/lib/persistRoute"
import { ActiveModuleColorProvider, useActiveModuleColor } from "@/contexts/ActiveModuleColorContext"
import { OrgStatusGuard } from "@/components/OrgStatusGuard"
import { NewSessionAlertBanner } from "@/components/auth/NewSessionAlertBanner"

interface LayoutProps {
  children: React.ReactNode
}

function LayoutContent({ children }: LayoutProps) {
  const location = useLocation()
  const { switchingOrg } = useOrganizationContext()
  const { state, isMobile, setOpenMobile } = useSidebar()
  const isCollapsed = state === "collapsed"
  const { color: activeModuleColor, previewMode } = useActiveModuleColor()

  // Salvar página atual para restaurar após atualizar/refresh
  useEffect(() => {
    if (isAppPath(location.pathname)) {
      localStorage.setItem(FLUITY_LAST_PATH_KEY, location.pathname + (location.search || ""))
    }
  }, [location.pathname, location.search])

  return (
    <div className="min-h-screen flex w-full bg-background overflow-x-hidden">
      <div
        className={cn(
          'fixed top-0 left-0 right-0 z-[200] transition-all duration-700',
          previewMode ? 'h-[5px] preview-bar-active' : 'h-[4px]',
        )}
        style={previewMode ? undefined : { backgroundColor: activeModuleColor }}
      />
      <UnifiedSidebar />

      {isMobile && (
        <button
          onClick={() => setOpenMobile(true)}
          aria-label="Abrir menu"
          className="fixed top-3 left-3 z-50 flex h-10 w-10 items-center justify-center rounded-xl border border-border/50 bg-background/95 text-muted-foreground shadow-md backdrop-blur-sm transition-all hover:border-primary/40 hover:text-primary active:scale-95 md:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
      )}

      <div className={cn(
        "flex-1 flex flex-col min-w-0 transition-all duration-300 w-full",
        isCollapsed ? "md:pl-16" : "md:pl-64"
      )}>
        <NewSessionAlertBanner />
        <main className="flex-1 relative p-3 sm:p-4 md:p-6 overflow-x-hidden pt-16 md:pt-6">
          <OrgStatusGuard>
          {switchingOrg && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="flex items-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Trocando organização...</span>
              </div>
            </div>
          )}
          {children}
          </OrgStatusGuard>
        </main>
        <WebChatFloatingWidget />
        <DocAssistantWidget />
        <ProfileCompletionPopup />
      </div>
    </div>
  )
}

export function Layout({ children }: LayoutProps) {
  return (
    <ProtectedRoute>
      <ActiveModuleColorProvider>
        <SidebarProvider defaultOpen={false}>
          <LayoutContent>{children}</LayoutContent>
        </SidebarProvider>
      </ActiveModuleColorProvider>
    </ProtectedRoute>
  )
}
